import type { State as CustomerState } from '../../state-management/customer/state.type';
import type { Project } from '../../state-management/project/types/project.type';

export type CrmValidationResult = {
	canProceed: boolean;
	blockingErrors: Array<string>;
	warningMessage: string | null;
};

export type ValidateCrmOperationParams = {
	customer: CustomerState;
	project: Array<Project>;
	selectedDealId?: string | null;
};

const isEmpty = (value: string | undefined | null): boolean => !value || value.trim() === '';

export const validateCrmOperation = (params: ValidateCrmOperationParams): CrmValidationResult => {
	const { customer, project, selectedDealId } = params;
	const blockingErrors: Array<string> = [];

	// Blocking: no customer loaded
	if (!customer.email) {
		blockingErrors.push('No customer is loaded. Use the "Find Customer" button in the toolbar to search for and load a customer by email or name.');
		return { canProceed: false, blockingErrors, warningMessage: null };
	}

	// Blocking: no deals exist
	if (project.length === 0) {
		blockingErrors.push('This customer has no deals in HubSpot. Create a deal using the "Associated Deal" button in the HubSpot Options panel, or create one directly in HubSpot.');
		return { canProceed: false, blockingErrors, warningMessage: null };
	}

	// Blocking: deals exist but none is selected — submitting with an empty dealId silently breaks the
	// HubSpot deal association (blueprint + quote never attach), so require an explicit selection.
	if (isEmpty(selectedDealId)) {
		blockingErrors.push('Please select the deal to submit this blueprint to, using the "Associated Deal" dropdown. If none fits, create a deal in HubSpot and reload the customer.');
		return { canProceed: false, blockingErrors, warningMessage: null };
	}

	// Resolve the selected deal
	const deal = selectedDealId
		? project.find((d) => d.dealId === selectedDealId) ?? project[0]
		: project[0];

	// Blocking: no zip code on deal or customer
	const hasZip = !isEmpty(deal?.zip) || !isEmpty(customer.zipcode);
	if (!hasZip) {
		blockingErrors.push('A zip code is required for tax calculation but none was found on the customer record or the associated deal. Add a zip code in HubSpot, then reload the customer.');
	}

	// Collect all missing fields into a single combined message
	const missingParts: Array<string> = [];

	const missingCustomerFields: Array<string> = [];
	if (isEmpty(customer.firstName)) missingCustomerFields.push('First Name');
	if (isEmpty(customer.lastName)) missingCustomerFields.push('Last Name');
	if (isEmpty(customer.address)) missingCustomerFields.push('Address');
	if (isEmpty(customer.city)) missingCustomerFields.push('City');
	if (isEmpty(customer.state)) missingCustomerFields.push('State');
	if (isEmpty(customer.phone)) missingCustomerFields.push('Phone');

	if (missingCustomerFields.length > 0) {
		missingParts.push(`Customer: ${missingCustomerFields.join(', ')}`);
	}

	const missingDealFields: Array<string> = [];
	if (deal && isEmpty(deal.address)) missingDealFields.push('Address');
	if (deal && isEmpty(deal.city)) missingDealFields.push('City');
	if (deal && isEmpty(deal.state)) missingDealFields.push('State');

	if (missingDealFields.length > 0) {
		missingParts.push(`Deal: ${missingDealFields.join(', ')}`);
	}

	const warningMessage = missingParts.length > 0
		? `Missing fields — ${missingParts.join(' | ')}`
		: null;

	return {
		canProceed: blockingErrors.length === 0,
		blockingErrors,
		warningMessage
	};
};
